import { createLogger } from "./logger.js";
/**
 * Per-agent LLM usage telemetry.
 *
 * Every agent call site wraps its `client.messages.parse(...)` in
 * `recordUsage(agent, model, response, latencyMs)`. Production telemetry
 * is the `llm.request` span (tokens/latency tags) and the dogstatsd sink
 * below; the per-call `llm.usage` line is debug, off by default, surfacing
 * only under `LOG_LEVEL=debug` for eyeballing a single local call.
 *
 * Cache telemetry: `cache_read_input_tokens` and
 * `cache_creation_input_tokens` populate when an agent uses
 * `cache_control: { type: "ephemeral" }` on its system prompt. As of
 * Sprint I, three agents do: heartbeat-triage, microstep.generator,
 * brief.composer.batch. Aggregating these by agent gives the
 * cache-hit ratio - if it stays low for an agent, that agent's call
 * pattern doesn't cluster within the 5-min TTL and the
 * cache_control should be removed (write cost > read savings).
 *
 * Observer hook: in addition to the structured log line, every call
 * fans out to an optional `LlmUsageSink` registered by the host app.
 * Apps wire this to their Datadog dogstatsd client so the same data
 * lands in metrics (counters + histograms) for dashboarding without
 * coupling utils to dd-trace. Tests can pass a custom sink to assert
 * on emitted events.
 */
const log = createLogger({ component: "llm-usage" });
let sink = null;
/**
 * Register a sink to receive a structured event for every successful
 * LLM call. Calling again replaces the previous sink (the host app
 * owns the lifecycle). Pass `null` or call `clearLlmUsageSink()` to
 * disable.
 */
export function setLlmUsageSink(next) {
    sink = next;
}
export function clearLlmUsageSink() {
    sink = null;
}
/**
 * Anthropic SDK responses carry a `usage` field on every successful
 * message. Both `messages.create` and `messages.parse` return the
 * same shape; pass either.
 */
export function recordUsage(agent, model, response, latencyMs, extra = {}) {
    const u = response.usage ?? {};
    const event = {
        agent,
        model,
        inputTokens: u.input_tokens ?? 0,
        outputTokens: u.output_tokens ?? 0,
        cacheReadTokens: u.cache_read_input_tokens ?? 0,
        cacheWriteTokens: u.cache_creation_input_tokens ?? 0,
        latencyMs,
        extra,
    };
    log.debug("llm.usage", {
        agent: event.agent,
        model: event.model,
        input_tokens: event.inputTokens,
        output_tokens: event.outputTokens,
        cache_read: event.cacheReadTokens,
        cache_write: event.cacheWriteTokens,
        latency_ms: event.latencyMs,
        ...event.extra,
    });
    if (sink) {
        try {
            sink(event);
        }
        catch (err) {
            log.error("llm.usage_sink_failed", {
                err: err instanceof Error ? err.message : String(err),
            });
        }
    }
}
/**
 * Convenience: time an awaited LLM call + record usage in one shot.
 * Returns the call's result. The agent's catch path doesn't see this
 * - callers wrap their own try/catch around the whole thing.
 */
export async function withUsage(agent, model, fn, extra = {}) {
    const start = Date.now();
    const result = await fn();
    recordUsage(agent, model, result, Date.now() - start, extra);
    return result;
}
//# sourceMappingURL=llm-usage.js.map